import React, { useEffect, useState } from 'react';

export default function Success({ onContinue }) {
  const [plan, setPlan] = useState('');
  const [count, setCount] = useState(10);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const planName = params.get('plan');
    if (planName) setPlan(planName);

    const timer = setInterval(() => {
      setCount(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onContinue();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onContinue]);

  const planDetails = {
    Professional: {
      price: '$399/mo',
      setup: '$499.00',
      features: ['All 3 CMMC levels + NIST 800-171', 'SSP and POA&M PDF export', 'AI compliance assistant', 'Policy template library', 'Up to 3 team members'],
    },
    Business: {
      price: '$799/mo',
      setup: '$799.00',
      features: ['Everything in Professional', 'All 8 compliance frameworks', 'Unlimited team seats', 'Dedicated account manager', 'Priority phone support'],
    },
  };

  const details = planDetails[plan] || planDetails.Professional;

  const nextSteps = [
    {
      number: '01',
      title: 'Complete your profile',
      description: 'Add your company name, CAGE code, and contact information so it appears in your SSP documents.',
    },
    {
      number: '02',
      title: 'Run your first assessment',
      description: 'Answer the self-assessment questions to identify your current compliance gaps across all CMMC domains.',
    },
    {
      number: '03',
      title: 'Upload evidence files',
      description: 'Upload your existing policies, procedures, and documentation to map them to CMMC controls.',
    },
    {
      number: '04',
      title: 'Export your SSP',
      description: 'Generate your System Security Plan and POA&M to share with your C3PAO assessor.',
    },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      fontFamily: 'var(--font)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
    }}>
      <div style={{ maxWidth: 600, width: '100%' }}>

        {/* Success Icon */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'var(--green-bg)', border: '2px solid var(--green)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', fontSize: 32,
          }}>
            <div style={{
              width: 36, height: 36, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <svg viewBox="0 0 24 24" fill="none" width="36" height="36">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="#1DB87A"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          <div style={{
            fontSize: 24, fontWeight: 600,
            color: 'var(--text)', marginBottom: 8,
          }}>
            Payment successful
          </div>
          <div style={{ fontSize: 14, color: 'var(--text3)', lineHeight: 1.6 }}>
            Welcome to TheClearAudit{plan ? ` ${plan}` : ''}. Your account is active and ready to use.
          </div>
        </div>

        {/* Plan Summary */}
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--green)',
          borderRadius: 10, padding: 20, marginBottom: 20,
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: 14,
            paddingBottom: 12, borderBottom: '1px solid var(--border)',
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                {plan || 'Pro'} Plan
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                Active — renews monthly
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{
                fontSize: 18, fontWeight: 700, color: 'var(--accent)',
              }}>
                {details.price}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                {details.setup} setup fee charged today
              </div>
            </div>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
          }}>
            {details.features.map(f => (
              <div key={f} style={{
                display: 'flex', alignItems: 'center', gap: 7,
                fontSize: 12, color: 'var(--text2)',
              }}>
                <div style={{
                  width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                  background: 'var(--green-bg)', border: '1px solid var(--green)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 8, fontWeight: 700, color: 'var(--green)',
                }}>v</div>
                {f}
              </div>
            ))}
          </div>
        </div>

        {/* What Happens Next */}
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 20, marginBottom: 24,
        }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: 'var(--text)',
            marginBottom: 16, paddingBottom: 10,
            borderBottom: '1px solid var(--border)',
          }}>
            What happens next
          </div>
          {nextSteps.map((step, i) => (
            <div key={step.number} style={{
              display: 'flex', gap: 14, marginBottom: i < nextSteps.length - 1 ? 14 : 0,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700, color: 'var(--accent)',
                fontFamily: 'var(--mono)',
              }}>
                {step.number}
              </div>
              <div>
                <div style={{
                  fontSize: 12, fontWeight: 600,
                  color: 'var(--text)', marginBottom: 3,
                }}>
                  {step.title}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
                  {step.description}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Email Notice */}
        <div style={{
          background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.2)',
          borderRadius: 8, padding: '12px 16px', marginBottom: 24,
          fontSize: 12, color: 'var(--accent)', lineHeight: 1.5, textAlign: 'center',
        }}>
          A receipt has been sent to your email address.
          Check your inbox for your payment confirmation from Stripe.
        </div>

        {/* Continue Button */}
        <button
          onClick={onContinue}
          style={{
            width: '100%', padding: '12px', borderRadius: 6,
            fontSize: 14, fontWeight: 600, cursor: 'pointer',
            border: '1px solid var(--accent)',
            background: 'var(--accent)', color: '#fff',
            fontFamily: 'var(--font)', transition: 'opacity 0.15s',
          }}
        >
          Go to Dashboard ({count})
        </button>

        <div style={{
          textAlign: 'center', marginTop: 14,
          fontSize: 11, color: 'var(--text3)',
        }}>
          Need help? Email us at theclearaudit@gmail.com
        </div>

      </div>
    </div>
  );
}